import type { QueryKey } from '@tanstack/react-query';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, useSearch } from '@tanstack/react-router';
import { AGGREGATE_NAMES, LOAN_REQUEST_RESOLVERS } from 'cpool-constants';
import { type Schema_OfferFilter, type Schema_OfferFilterCriteria, type Schema_OfferFilterStatus } from 'cpool-schemas';
import { schemaLoanRequestResolver_GetAcceptedOffers } from 'cpool-tables/backend-resolvers';

import { useAggregateQuery } from '~/queryUtils/useQuery';
import { useQueryPositionStore } from '~/store/queryPositionStore.ts';
import { appendPositionToQueryKey } from '~/utils/appendPositionToQueryKey.ts';
import { queryKeys } from '~/utils/queryKeys';

type BuildFilterResult = {
    queryKey: QueryKey | undefined;
    filter: {
        duplicates?: boolean;
        legalConsent?: boolean;
        offerFilter?: Schema_OfferFilter;
        offerInfoId?: string;
        schufa?: boolean;
    };
};

export const useAcceptedOffers = (loanRequestId: string) => {
    const { useNavigate } = getRouteApi('/_app/leads_/details/$leadId/offer-management');
    const search = useSearch({ strict: false });
    const offerFilter = 'offerFilter' in search ? search.offerFilter : {};
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const queryKey = queryKeys.getAcceptedOffers(loanRequestId, offerFilter);

    const queryPosition = useQueryPositionStore((state) => state.positions[JSON.stringify(queryKey)]);

    const buildFilter = (prev: BuildFilterResult['filter'], filter: Schema_OfferFilter): BuildFilterResult => {
        if (filter) {
            const clone = structuredClone(filter);
            const { criteria, status } = clone;

            if (criteria) {
                Object.keys(criteria).forEach((key) => {
                    // @ts-expect-error criteria is a dynamic object
                    if (criteria[key] === undefined) delete criteria[key];
                });
                if (Object.keys(criteria).length === 0) delete clone.criteria;
            } else delete clone.criteria;

            if ((status && status.length === 0) || !status) delete clone.status;

            if (Object.keys(clone).length > 0)
                return {
                    filter: { ...prev, offerFilter: clone },
                    queryKey: appendPositionToQueryKey.current(
                        queryKeys.getAcceptedOffers(loanRequestId, clone as Schema_OfferFilter),
                    ),
                };
        }

        const prevClone = structuredClone(prev);
        if ('offerFilter' in prevClone) delete prevClone.offerFilter;
        return {
            filter: prevClone,
            queryKey: appendPositionToQueryKey.current(queryKeys.getAcceptedOffers(loanRequestId)),
        };
    };

    const setCriteriaFilter = async (criteriaFilter: Schema_OfferFilterCriteria) => {
        let result: BuildFilterResult = { filter: {}, queryKey: undefined };
        // when we have no criteria filter anymore, we want to also remove the status filter
        const offerFilters = Object.keys(criteriaFilter).length === 0 ? {} : offerFilter;
        await navigate({
            search: (prev) => {
                result = buildFilter(prev, { ...offerFilters, criteria: criteriaFilter });
                return result.filter;
            },
        });
        await queryClient.invalidateQueries({
            queryKey: result.queryKey as QueryKey,
        });
    };

    const setStatusFilter = async (statusFilter: Schema_OfferFilterStatus) => {
        let result: BuildFilterResult = { filter: {}, queryKey: undefined };
        await navigate({
            search: (prev) => {
                result = buildFilter(prev, { ...offerFilter, status: statusFilter });
                return result.filter;
            },
        });
        await queryClient.invalidateQueries({
            queryKey: result.queryKey as QueryKey,
        });
    };

    const getCriteriaFilter = () => {
        return offerFilter?.criteria ? offerFilter.criteria : {};
    };

    const getStatusFilter = () => {
        return offerFilter?.status ? offerFilter.status : [];
    };

    const queryResult = useAggregateQuery({
        acl: { loanRequestId },
        aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
        resolverName: LOAN_REQUEST_RESOLVERS.getAcceptedOffers,
        schema: schemaLoanRequestResolver_GetAcceptedOffers,
        searchParams: { filter: offerFilter || null, id: loanRequestId, position: queryPosition },
        tanstack: {
            placeholderData: keepPreviousData,
            // refetch the data every 30 sec for the status
            refetchInterval: 30_000,
        },
    });

    return { ...queryResult, getCriteriaFilter, getStatusFilter, setCriteriaFilter, setStatusFilter };
};
